<script setup lang="ts">
import { toEmbeddableImage, useContestPreviewFrame } from '../composables/contestPreviewFrame'

import type { Boost } from './types/global'
import type { ContestMechanic } from '@/types/global'
import type { Contest } from '@/types/types'

const props = withDefaults(
  defineProps<{
    contest: Contest
    mechanic?: ContestMechanic
    selectedBoost: Boost
  }>(),
  {
    mechanic: 'fortuneWheel'
  }
)

const { t } = useI18n()
const { merchantLogo, merchantName, user } = useLocalStorage()

const buildPayload = async () => {
  const [logoUrl, coverImageUrl] = await Promise.all([
    toEmbeddableImage(props.contest.logo || props.contest.logoUrl || merchantLogo.value),
    toEmbeddableImage(props.contest.coverImage || props.contest.coverImageUrl)
  ])

  return {
    contest: {
      ...props.contest,
      logo: null,
      coverImage: null,
      logoUrl,
      coverImageUrl,
      stores: []
    },
    mechanic: props.mechanic,
    boostSlug: props.selectedBoost?.id || 'none',
    merchant: {
      id: user.value.merchant.id,
      name: merchantName.value,
      logo: merchantLogo.value,
      webAppUrl: user.value.merchant.webAppUrl
    },
    storeName: merchantName.value
  }
}

const { iframeRef, previewUrl, debouncedSendPreviewData, resetPreview } = useContestPreviewFrame(buildPayload)

watch(
  () => [props.contest, props.mechanic, props.selectedBoost],
  () => {
    debouncedSendPreviewData()
  },
  { deep: true }
)
</script>

<template>
  <div class="rounded-xl bg-white border lg:sticky lg:top-6 xl:w-1/3 xl:min-w-[372px] overflow-hidden">
    <div class="border-b p-4 flex items-center justify-between gap-4">
      <h3 class="font-semibold text-foreground">{{ t('pages.contests.preview.title') }}</h3>

      <Button variant="outline" size="sm" @click="resetPreview">
        {{ t('contests.buttons.tryAgain') }}
      </Button>
    </div>

    <div v-if="previewUrl" class="p-4">
      <BasePhone size="md">
        <iframe
          ref="iframeRef"
          :src="previewUrl"
          class="w-full h-full"
          title="Contest preview"
          sandbox="allow-same-origin allow-scripts allow-modals allow-popups"
        />
      </BasePhone>
    </div>
  </div>
</template>
